import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import { Add, Refresh, SwapHoriz } from '@mui/icons-material';
import { P, cardSx, fieldSx, priBtn, outBtn, thSx, thTitle, PX, StatusPill } from '../../components/common/workspaceKit';
import { formatIndianNumber } from '../../utils/currency';
import { linkNav } from '../../utils/linkNav';
import ExportButton from '../../components/common/ExportButton';
import {
  materialConversionService, ConversionListRow, ConversionType, CONVERSION_TYPE_LABELS,
} from '../../services/materialConversionService';

/**
 * Material conversions.
 *
 * The loss column is deliberately prominent: a conversion that quietly loses
 * value is the one worth looking at, and before this module existed there was
 * no place that loss could even be recorded.
 */

const fmtDate = (v?: string | null) =>
  (v ? new Date(v).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—');

const MaterialConversionListPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ConversionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    materialConversionService.list(type ? { conversionType: type } : {})
      .then((r) => setRows(r.conversions || []))
      .catch((e: any) => setError(e.message || 'Failed to load conversions'))
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(load, [load]);

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100%', pb: 5 }}>
      <Box sx={{ px: PX, py: 2, borderBottom: `1px solid ${P.line}`, bgcolor: '#fff' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography sx={{ fontSize: '21px', fontWeight: 800, color: P.strong, letterSpacing: '-.02em' }}>
              Material Conversions
            </Typography>
            <Typography sx={{ fontSize: '13.5px', color: P.muted, mt: '2px' }}>
              Reclassification, grade change, repacking and in-house conversion — recorded as transactions,
              not as edits to the product master.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <TextField select size="small" label="Type" value={type}
              onChange={(e) => setType(e.target.value)} sx={{ ...fieldSx, minWidth: 200 }}>
              <MenuItem value="">All types</MenuItem>
              {(Object.keys(CONVERSION_TYPE_LABELS) as ConversionType[]).map((t) => (
                <MenuItem key={t} value={t}>{CONVERSION_TYPE_LABELS[t]}</MenuItem>
              ))}
            </TextField>
            <ExportButton
              rows={rows as unknown as Record<string, unknown>[]}
              columns={[
                { header: 'Number', key: 'conversion_number' },
                { header: 'Date', key: 'conversion_date' },
                { header: 'Type', key: 'conversion_type' },
                { header: 'Reason', key: 'reason' },
                { header: 'Consumed', key: 'total_source_value' },
                { header: 'Received', key: 'total_dest_value' },
                { header: 'Loss', key: 'loss_value' },
                { header: 'Status', key: 'status' },
              ]}
              filename="material-conversions"
            />
            <Button variant="outlined" startIcon={<Refresh />} onClick={load} sx={outBtn}>Refresh</Button>
            <Button variant="contained" startIcon={<Add />} sx={priBtn} {...linkNav('/inventory/conversions/new', navigate)}>
              New Conversion
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: PX, py: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}><Typography sx={thTitle}>Conversion</Typography></TableCell>
                    <TableCell sx={thSx}><Typography sx={thTitle}>Type</Typography></TableCell>
                    <TableCell sx={thSx}><Typography sx={thTitle}>Reason</Typography></TableCell>
                    <TableCell align="right" sx={thSx}><Typography sx={thTitle}>Lines</Typography></TableCell>
                    <TableCell align="right" sx={thSx}><Typography sx={thTitle}>Consumed</Typography></TableCell>
                    <TableCell align="right" sx={thSx}><Typography sx={thTitle}>Received</Typography></TableCell>
                    <TableCell align="right" sx={thSx}><Typography sx={thTitle}>Loss</Typography></TableCell>
                    <TableCell sx={thSx}><Typography sx={thTitle}>Status</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} {...linkNav(`/inventory/conversions/${r.id}`, navigate)}>
                      <TableCell>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: P.primary, fontFamily: 'monospace' }}>
                          {r.conversion_number}
                        </Typography>
                        <Typography sx={{ fontSize: '11.5px', color: P.muted }}>
                          {fmtDate(r.conversion_date)}{r.source_warehouse_name ? ` · ${r.source_warehouse_name}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={CONVERSION_TYPE_LABELS[r.conversion_type] || r.conversion_type}
                          sx={{ height: 21, fontSize: '11.5px', fontWeight: 700, bgcolor: P.primarySoft, color: P.primary }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', color: P.text, maxWidth: 280 }}>
                        <Typography noWrap sx={{ fontSize: '13px' }}>{r.reason || r.reason_code || '—'}</Typography>
                        {r.rule_name && (
                          <Typography sx={{ fontSize: '11px', color: P.muted }}>via {r.rule_name}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px' }}>{r.line_count}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>
                        {formatIndianNumber(Number(r.total_source_value))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 600, color: P.strong, fontVariantNumeric: 'tabular-nums' }}>
                        {formatIndianNumber(Number(r.total_dest_value))}
                      </TableCell>
                      <TableCell align="right" sx={{
                        fontSize: '13px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        color: Number(r.loss_value) > 0 ? P.red : P.muted,
                      }}>
                        {Number(r.loss_value) > 0 ? `${formatIndianNumber(Number(r.loss_value))}` : '—'}
                      </TableCell>
                      <TableCell>
                        <StatusPill label={r.status}
                          tone={r.status === 'POSTED' ? 'green' : r.status === 'CANCELLED' ? 'red' : 'amber'} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {!rows.length && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}>
                        <SwapHoriz sx={{ fontSize: 34, color: P.border, mb: 1 }} />
                        <Typography sx={{ fontSize: '14px', color: P.muted, mb: 1.5 }}>
                          No conversions yet. Use this instead of editing a product's type — that rewrites
                          history and moves no stock.
                        </Typography>
                        <Button variant="contained" startIcon={<Add />} sx={priBtn} {...linkNav('/inventory/conversions/new', navigate)}>
                          New Conversion
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default MaterialConversionListPage;
